import { Context, createContext, CSSProperties, ReactNode, useContext } from 'react';
import { Simplify } from 'type-fest';
import { PlainNavigationItemProps } from '../components/NavigationItem/PlainNavigationItem';

// Global type that gets overridden by adapters
declare global {
  // eslint-disable-next-line ts/ban-ts-comment
  // @ts-ignore -- competing global declarations
  interface MdsNavigationItemProps {}

  // Style is the only required prop (used in HomePageLink)
  // eslint-disable-next-line ts/ban-ts-comment
  // @ts-ignore -- competing global declarations
  interface MdsAdapterLinkProps {
    style: CSSProperties;
    children: ReactNode;
  }
}

export type NavigationItemProps<TComponentProps = unknown> = TComponentProps extends object
  ? Omit<PlainNavigationItemProps, 'component' | keyof TComponentProps> & TComponentProps
  : Omit<PlainNavigationItemProps, 'component' | keyof MdsNavigationItemProps> & MdsNavigationItemProps;

export interface RouterAdapter {
  NavigationItemComponent: (props: NavigationItemProps<unknown>) => ReactNode;
  LinkComponent: (props: Simplify<MdsAdapterLinkProps>) => ReactNode;
  /**
   * Default home page link props (e.g. `to` or `href` depending on the router)
   */
  homePageLinkProps: Partial<MdsAdapterLinkProps>;
}

export const AdapterContext: Context<RouterAdapter> = createContext<RouterAdapter>({
  NavigationItemComponent() {
    throw new Error('@mds/app-layout requires a router adapter. Please see mds.marex.com for setup instructions');
  },
  LinkComponent() {
    throw new Error('@mds/app-layout requires a router adapter. Please see mds.marex.com for setup instructions');
  },
  homePageLinkProps: {},
});

AdapterContext.displayName = 'AppLayoutAdapter';

/**
 * Create a custom adapter for @mds/app-layout to work with your framework / router.
 */
export const createRouterAdapter = (obj: RouterAdapter): RouterAdapter => obj;

/**
 * Hook to get adapter context values
 */
export function useAdapterContext(): RouterAdapter {
  return useContext(AdapterContext);
}

/**
 * Hook to access the router adapter from @mds/app-layout.
 * Returns the current RouterAdapter containing LinkComponent, NavigationItemComponent
 * and homePageLinkProps.
 */
export function useAppLayoutRouterAdapter(): RouterAdapter {
  return useContext(AdapterContext);
}
